import { useState, useEffect } from 'react'
import { useDocumentTitle } from '../hooks/useDocumentTitle'
import { useNavigate } from 'react-router-dom'
import { Save, Loader2, ShieldCheck, AlertTriangle, RefreshCw, ChevronDown, BellRing, Send, Clock } from 'lucide-react'
import { useApi } from '../hooks/useApi'
import { useAuth } from '../hooks/useAuth'
import { useData } from '../hooks/useData'
import type { Settings as SettingsType } from '../types'
import { inputClass } from '../components/Field'
import LoadingSpinner from '../components/LoadingSpinner'
import Modal from '../components/Modal'
import SettingsTabs from '../components/SettingsTabs'
import { formatDate, formatDateTime } from '../lib/utils'

function BauSyncButton({ api }: { api: ReturnType<typeof useApi> }) {
  const [syncing, setSyncing] = useState(false)
  const [done, setDone] = useState(false)
  const handleSync = async () => {
    setSyncing(true); setDone(false)
    try { await api.triggerBauSync(); setDone(true) }
    finally { setSyncing(false) }
  }
  return done
    ? <span className="text-xs text-green-600 font-medium">Sync triggered. Data updates in ~1 min</span>
    : <button onClick={handleSync} disabled={syncing}
        className="flex items-center gap-2 px-3 py-1.5 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 transition-colors">
        {syncing ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Syncing…</> : <><ShieldCheck className="w-3.5 h-3.5" /> Sync now</>}
      </button>
}

function JiraSyncButton({ api }: { api: ReturnType<typeof useApi> }) {
  const [syncing, setSyncing] = useState(false)
  const [done, setDone] = useState(false)
  const handleSync = async () => {
    setSyncing(true); setDone(false)
    try { await api.triggerJiraSync(); setDone(true) }
    finally { setSyncing(false) }
  }
  return done
    ? <span className="text-xs text-green-600 font-medium">Sync triggered. Data updates in ~1 min</span>
    : <button onClick={handleSync} disabled={syncing}
        className="flex items-center gap-2 px-3 py-1.5 border border-slate-200 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 transition-colors">
        {syncing ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Syncing…</> : <><ShieldCheck className="w-3.5 h-3.5" /> Sync now</>}
      </button>
}

/**
 * Admin-only settings. Gated on the PlatformAdmin role on both the frontend
 * (redirect non-admins) and the backend (`requires_admin` on `PUT /settings`).
 * Holds integration secrets and the KPI tolerance knobs that affect how On Time
 * and At Cost are calculated app-wide. Who holds which role lives in Settings >
 * Permissions, since roles are held in Entra.
 */
export default function SettingsAdmin() {
  useDocumentTitle('Admin Settings')
  const api = useApi()
  const navigate = useNavigate()
  const { user, loading: authLoading } = useAuth()
  const isAdmin = !!user?.isAdmin

  // Non-admins who type the URL directly get bounced. Using an effect so the
  // redirect only fires after auth resolves (otherwise we'd flash the page
  // during the initial MSAL settle).
  useEffect(() => {
    if (!authLoading && !isAdmin) navigate('/settings', { replace: true })
  }, [authLoading, isAdmin, navigate])

  const { data, loading } = useData(() =>
    Promise.all([
      api.getSettings(),
      api.getAsanaSyncRuns().catch(() => []),
      api.getNextAsanaSyncRun().catch(() => ({ nextRunAt: null })),
      api.getNextReminderSends().catch(() => ({ ownerSendDate: null, groupSendDate: null })),
    ]).then(([settings, syncRuns, nextRun, nextReminder]) => ({ settings, syncRuns, nextRun, nextReminder }))
  )
  const [settings, setSettings] = useState<SettingsType | null>(null)
  const [saving, setSaving] = useState(false)
  const [savedModal, setSavedModal] = useState(false)
  const [errorModal, setErrorModal] = useState<string | null>(null)
  const [openRunId, setOpenRunId] = useState<string | null>(null)
  const [showAllRuns, setShowAllRuns] = useState(false)
  const [testSending, setTestSending] = useState(false)
  const [testResult, setTestResult] = useState<string | null>(null)
  const [confirmPhase, setConfirmPhase] = useState<'owner' | 'group' | null>(null)
  const [runningPhase, setRunningPhase] = useState<'owner' | 'group' | null>(null)

  useEffect(() => { if (data && !settings) setSettings(data.settings) }, [data])

  if (authLoading || loading || !settings || !isAdmin || !data) return <LoadingSpinner />

  const syncRuns: any[] = data.syncRuns ?? []
  const nextRunAt: string | null = data.nextRun?.nextRunAt ?? null
  const ownerSendDate: string | null = data.nextReminder?.ownerSendDate ?? null
  const groupSendDate: string | null = data.nextReminder?.groupSendDate ?? null

  const handleTestReminder = async () => {
    setTestSending(true)
    try {
      const result: any = await api.sendTestReminder()
      if (result?.sent?.length) {
        setTestResult(
          `Test reminder sent to ${result.sent[0]}${result.personalised ? ' with your in-flight projects' : ' (generic version - you own no in-flight projects)'}.`
        )
      } else {
        setErrorModal('The webhook did not accept the email - check the Power Automate URL and flow run history.')
      }
    } catch (err: any) {
      setErrorModal(err?.message || 'Failed to send test reminder')
    } finally {
      setTestSending(false)
    }
  }

  const runPhase = async (phase: 'owner' | 'group') => {
    setConfirmPhase(null)
    setRunningPhase(phase)
    try {
      const result: any = phase === 'owner' ? await api.runOwnerReminders() : await api.runGroupReminder()
      if (result?.skipped) {
        setErrorModal(
          result.skipped === 'no webhook'
            ? 'No Power Automate URL is configured - set the survey webhook above first.'
            : result.skipped === 'no group email'
              ? 'No group email is configured - set one above first.'
              : `Send skipped (${result.skipped}).`
        )
      } else if (phase === 'owner') {
        const n = result?.sent?.length ?? 0
        const f = result?.failed?.length ?? 0
        const ownerless = result?.ownerlessProjects ?? 0
        setTestResult(
          `Owner send complete: ${n} owner email${n !== 1 ? 's' : ''} sent` +
          (f ? `, ${f} failed` : '') +
          (ownerless ? `. ${ownerless} in-flight project${ownerless !== 1 ? 's have' : ' has'} no owner email and only the group nudge would cover them.` : '.')
        )
      } else {
        const ok = (result?.sent?.length ?? 0) > 0
        setTestResult(ok
          ? `Group send complete: nudge sent to ${result.sent[0]}.`
          : 'The webhook did not accept the group email - check the Power Automate URL and flow run history.')
      }
    } catch (err: any) {
      setErrorModal(err?.message || 'Failed to run the send')
    } finally {
      setRunningPhase(null)
    }
  }

  // Mirrors the API: the link is rendered as an <a href>, so only a web URL.
  const requestAccessUrlInvalid = !!settings?.requestAccessUrl && !/^https?:\/\/[^\s/]+/i.test(settings.requestAccessUrl.trim())

  const handleSave = async () => {
    setSaving(true)
    try {
      await api.updateSettings(settings)
      setSavedModal(true)
    } catch (err: any) {
      setErrorModal(err?.message || 'Failed to save admin settings')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="max-w-4xl">
      <Modal open={savedModal} title="Admin Settings Saved" message="Admin settings have been saved successfully." onConfirm={() => setSavedModal(false)} />
      <Modal open={!!errorModal} title="Save Failed" message={errorModal || ''} onConfirm={() => setErrorModal(null)} />

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-800">Settings</h1>
        <p className="text-slate-500">Configure integrations and defaults</p>
      </div>

      <SettingsTabs active="admin" />

      <div className="mb-4 flex items-start gap-2 px-3 py-2 bg-[#F88806]/10 border border-[#F88806]/30 rounded-lg text-xs text-[#8B4900]">
        <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
        <div>
          These settings affect the whole app. Webhook URL gates survey sends; tolerance
          changes move On Time / At Cost percentages for every project retroactively.
        </div>
      </div>

      <div className="space-y-6">
        <div className="card p-6">
          <h2 className="font-bold text-slate-800 mb-4">Survey Email Webhook</h2>
          <input
            type="url"
            value={settings.powerAutomateUrl || ''}
            onChange={e => setSettings({ ...settings, powerAutomateUrl: e.target.value })}
            className={inputClass}
            placeholder="https://prod-XX.westeurope.logic.azure.com/workflows/..."
          />
        </div>

        <div className="card p-6">
          <Modal open={!!testResult} title="Test Reminder Sent" message={testResult || ''} onConfirm={() => setTestResult(null)} />
          <Modal
            open={!!confirmPhase}
            variant="danger"
            title={confirmPhase === 'group' ? 'Run group send now?' : 'Run owner send now?'}
            message=""
            confirmLabel="Send for real"
            cancelLabel="Cancel"
            onConfirm={() => { if (confirmPhase) runPhase(confirmPhase) }}
            onCancel={() => setConfirmPhase(null)}
          />
          <h2 className="font-bold text-slate-800 mb-1 flex items-center gap-1.5">
            <BellRing className="w-4 h-4 text-slate-500" />
            Monthly Update Reminder
          </h2>
          <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm">
            <div className="flex items-center justify-between gap-4">
              <span className="text-slate-500">Next owner emails</span>
              <span className="font-semibold text-slate-700">{ownerSendDate ? formatDate(ownerSendDate) : '-'}</span>
            </div>
            <div className="flex items-center justify-between gap-4 mt-1">
              <span className="text-slate-500">Next group email</span>
              <span className="font-semibold text-slate-700">{groupSendDate ? formatDate(groupSendDate) : '-'}</span>
            </div>
            {!settings.reminderEnabled && (
              <p className="mt-2 text-[11px] text-amber-600">Scheduled dates shown, but automatic sending is currently off - enable it below.</p>
            )}
            {settings.reminderEnabled && !settings.reminderGroupEmail && (
              <p className="mt-2 text-[11px] text-slate-400">No group email set - only the owner emails will send.</p>
            )}
          </div>
          <label className="flex items-center gap-2 mb-4 text-sm font-medium text-slate-700 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={!!settings.reminderEnabled}
              onChange={e => setSettings({ ...settings, reminderEnabled: e.target.checked })}
              className="w-4 h-4 rounded"
              style={{ accentColor: '#005EB8' }}
            />
            Send the monthly reminder automatically
          </label>
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
            Group email (M365 group or distribution list)
          </label>
          <input
            type="email"
            value={settings.reminderGroupEmail || ''}
            onChange={e => setSettings({ ...settings, reminderGroupEmail: e.target.value || undefined })}
            className={inputClass}
            placeholder="it-data-northeurope@danone.com"
          />
          <p className="mt-1 text-[11px] text-slate-400">
            Leave empty to send owner emails only. Remember to hit Save Admin Settings below.
          </p>
          <button
            onClick={handleTestReminder}
            disabled={testSending}
            className="mt-4 flex items-center gap-2 px-3 py-2 text-sm font-semibold border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50 hover:text-slate-800 disabled:opacity-50"
          >
            {testSending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {testSending ? 'Sending…' : 'Send test to me'}
          </button>
          <p className="mt-1.5 text-[11px] text-slate-400">
            Goes to your signed-in email only - the group address is never used for tests.
          </p>

          <div className="mt-4 pt-4 border-t border-slate-100">
            <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-2">Replicate a scheduled send (real recipients)</p>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setConfirmPhase('owner')}
                disabled={!!runningPhase}
                className="flex items-center gap-2 px-3 py-2 text-sm font-semibold border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50 hover:text-slate-800 disabled:opacity-50"
              >
                {runningPhase === 'owner' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Run owner send now (-2)
              </button>
              <button
                onClick={() => setConfirmPhase('group')}
                disabled={!!runningPhase || !settings.reminderGroupEmail}
                title={!settings.reminderGroupEmail ? 'Set a group email above first' : undefined}
                className="flex items-center gap-2 px-3 py-2 text-sm font-semibold border border-slate-200 rounded-lg text-slate-600 hover:bg-slate-50 hover:text-slate-800 disabled:opacity-50"
              >
                {runningPhase === 'group' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Run group send now (month-end)
              </button>
            </div>
            <p className="mt-1.5 text-[11px] text-amber-600">
              These send live emails to <strong>all owners</strong> / <strong>everyone in the group</strong> - not just you. They ignore the date and the on/off toggle.
            </p>
          </div>
        </div>

        <div className="card p-6">
          <h2 className="font-bold text-slate-800 mb-4">KPI Tolerances</h2>
          <div className="grid grid-cols-2 gap-4 max-w-xl">
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">On Time grace (days)</label>
              <input
                type="number"
                min={0}
                value={settings.onTimeDays ?? 0}
                onChange={e => setSettings({ ...settings, onTimeDays: Number(e.target.value) })}
                className={inputClass}
                placeholder="30"
              />
              <p className="mt-1 text-[11px] text-slate-400">
                Projects going live up to this many days after their due date still count as On Time.
              </p>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">At Cost allowance (%)</label>
              <input
                type="number"
                min={0}
                step={0.1}
                value={settings.atCostPct ?? 0}
                onChange={e => setSettings({ ...settings, atCostPct: Number(e.target.value) })}
                className={inputClass}
                placeholder="10"
              />
              <p className="mt-1 text-[11px] text-slate-400">
                Projects spending up to this % over forecast still count as At Cost.
              </p>
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Forecast risk sensitivity</label>
              <select
                value={settings.riskForecastMinScore ?? 4}
                onChange={e => setSettings({ ...settings, riskForecastMinScore: Number(e.target.value) })}
                className={inputClass}
              >
                <option value={1}>Any open risk (most conservative)</option>
                <option value={4}>Amber and above (recommended)</option>
                <option value={9}>Red only</option>
              </select>
              <p className="mt-1 text-[11px] text-slate-400">
                In the Reports Forecast view, how serious an open risk must be to project an OTIFAC miss. A delivery risk dents On Time, scope dents In Full, cost dents At Cost - each once it hits this rating (Impact x Likelihood).
              </p>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <h2 className="font-bold text-slate-800 mb-4">Reports Goal</h2>
          <div className="grid grid-cols-3 gap-4 max-w-2xl">
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Goal Hours Saved</label>
              <input
                type="number"
                value={settings.valueRealisedTargetHrs || 0}
                onChange={e => setSettings({ ...settings, valueRealisedTargetHrs: Number(e.target.value) })}
                className={inputClass}
                placeholder="100000"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Start Year</label>
              <input
                type="number"
                value={settings.valueRealisedTargetStartYear || ''}
                onChange={e => setSettings({ ...settings, valueRealisedTargetStartYear: e.target.value ? Number(e.target.value) : undefined })}
                className={inputClass}
                placeholder="2025"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">End Year</label>
              <input
                type="number"
                value={settings.valueRealisedTargetEndYear || ''}
                onChange={e => setSettings({ ...settings, valueRealisedTargetEndYear: e.target.value ? Number(e.target.value) : undefined })}
                className={inputClass}
                placeholder="2027"
              />
            </div>
          </div>
        </div>

        <div className="card p-6">
          <h2 className="font-bold text-slate-800 mb-1">Request Access Link</h2>
          <p className="text-xs text-slate-500 mb-3">The form behind the "Request access" link in the sidebar. Leave empty to hide the link.</p>
          <input
            type="url"
            value={settings.requestAccessUrl || ''}
            onChange={e => setSettings({ ...settings, requestAccessUrl: e.target.value || undefined })}
            className={inputClass}
            placeholder="https://danone.service-now.com/..."
          />
          {requestAccessUrlInvalid && (
            <p className="mt-1 text-[11px] text-[#B8242E]">Use an http or https link.</p>
          )}
        </div>

        <button onClick={handleSave} disabled={saving || requestAccessUrlInvalid} className="btn-primary flex items-center gap-2">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {saving ? 'Saving...' : 'Save Admin Settings'}
        </button>

        <div className="card p-6">
          <div className="flex items-center justify-between mb-1">
            <h2 className="font-bold text-slate-800 flex items-center gap-1.5">
              <RefreshCw className="w-4 h-4 text-slate-500" />
              Asana Sync
            </h2>
            <span className="text-xs text-slate-500">
              Next run: <span className="font-semibold text-slate-700">
                {nextRunAt ? formatDateTime(nextRunAt) : '-'}
              </span>
            </span>
          </div>
          <p className="text-sm text-slate-500 mb-4">
            The daily Asana sync runs at 01:00 UTC and pulls field changes
            into linked projects. Last 30 days of runs are kept.
          </p>

          {syncRuns.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-6">No sync runs recorded yet.</p>
          ) : (
            <div className="space-y-2">
              {(showAllRuns ? syncRuns : syncRuns.slice(0, 1)).map(run => {
                const runId: string = run.id ?? run.startedAt
                const summary = run.summary || {}
                const isOpen = openRunId === runId
                const wouldSync = (run.results as any[] | undefined)?.filter((r: any) => r.status === 'synced') ?? []
                const errors = (run.results as any[] | undefined)?.filter((r: any) => r.status === 'error') ?? []
                const skipped = (run.results as any[] | undefined)?.filter((r: any) => r.status === 'skipped') ?? []
                return (
                  <div key={runId} className="border border-slate-200 rounded-lg overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setOpenRunId(isOpen ? null : runId)}
                      className="w-full flex items-center justify-between gap-3 px-4 py-2.5 bg-white hover:bg-slate-50 text-left"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="text-sm font-semibold text-slate-700 whitespace-nowrap">{formatDateTime(run.startedAt)}</span>
                        <span className="text-[10px] uppercase tracking-wide font-bold text-slate-400">{run.trigger || 'scheduled'}</span>
                      </div>
                      <div className="flex items-center gap-3 text-xs">
                        <span className="text-emerald-700 font-semibold">{summary.synced ?? 0} synced</span>
                        <span className="text-slate-500">{summary.no_changes ?? 0} no change</span>
                        {(summary.errors ?? 0) > 0 && (
                          <span className="text-red-700 font-semibold">{summary.errors} errors</span>
                        )}
                        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                      </div>
                    </button>

                    {isOpen && (
                      <div className="px-4 py-3 bg-slate-50 border-t border-slate-200 space-y-3">
                        {wouldSync.length > 0 && (
                          <div>
                            <h3 className="font-semibold text-xs text-slate-700 mb-2">Synced ({wouldSync.length})</h3>
                            <div className="space-y-2">
                              {wouldSync.map((r: any) => (
                                <div key={r.projectId} className="bg-white border border-slate-200 rounded p-2.5">
                                  <div className="font-medium text-slate-800 text-xs mb-1">{r.projectName}</div>
                                  <table className="w-full text-[11px]">
                                    <tbody>
                                      {Object.entries(r.changes as Record<string, { from: any; to: any }>).map(([field, diff]) => (
                                        <tr key={field} className="border-t border-slate-50">
                                          <td className="py-0.5 pr-3 text-slate-600 font-mono">{field}</td>
                                          <td className="py-0.5 pr-3"><code className="bg-red-50 text-red-700 px-1 rounded">{JSON.stringify(diff.from)}</code></td>
                                          <td className="py-0.5"><code className="bg-emerald-50 text-emerald-700 px-1 rounded">{JSON.stringify(diff.to)}</code></td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {errors.length > 0 && (
                          <div>
                            <h3 className="font-semibold text-xs text-red-700 mb-1">Errors ({errors.length})</h3>
                            <div className="space-y-1">
                              {errors.map((r: any) => (
                                <div key={r.projectId} className="text-[11px] bg-red-50 border border-red-100 rounded px-2 py-1">
                                  <span className="font-medium text-slate-800">{r.projectName}</span>
                                  <span className="text-red-700 ml-2">{r.error}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {skipped.length > 0 && (
                          <details>
                            <summary className="text-xs font-semibold text-slate-600 cursor-pointer hover:text-slate-800">
                              Skipped ({skipped.length})
                            </summary>
                            <div className="mt-1 space-y-0.5 text-[11px]">
                              {skipped.map((r: any) => (
                                <div key={r.projectId} className="flex justify-between bg-white rounded px-2 py-1">
                                  <span className="text-slate-700">{r.projectName}</span>
                                  <span className="text-slate-400 font-mono">{r.reason}</span>
                                </div>
                              ))}
                            </div>
                          </details>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
              {syncRuns.length > 1 && (
                <button
                  type="button"
                  onClick={() => setShowAllRuns(v => !v)}
                  className="w-full text-xs text-slate-500 hover:text-slate-700 py-1.5 text-center"
                >
                  {showAllRuns ? 'Show less' : `Show older runs (${syncRuns.length - 1})`}
                </button>
              )}
            </div>
          )}
        </div>

        <div className="card p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-slate-400" />
              <div>
                <h2 className="font-bold text-slate-800">Operational BAU: ServiceNow Sync</h2>
                <p className="text-xs text-slate-500 mt-0.5">Syncs daily at 09:00 UTC from Power BI. Trigger manually if needed.</p>
              </div>
            </div>
            <BauSyncButton api={api} />
          </div>
          <div className="flex items-center justify-between border-t border-slate-100 pt-4">
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-slate-400" />
              <div>
                <h2 className="font-bold text-slate-800">Operational BAU: Jira Sync</h2>
                <p className="text-xs text-slate-500 mt-0.5">Pulls Jira (Commercial AEM BAU) issues daily at 09:15 UTC. Trigger manually if needed.</p>
              </div>
            </div>
            <JiraSyncButton api={api} />
          </div>
        </div>

        {/* The user directory and who holds which role moved to Settings >
            Permissions, where roles are shown against the Entra groups that
            grant them. */}
      </div>

    </div>
  )
}
